import { useState } from 'react'
import {
  EQUIPMENT_LABEL, MAX_CUSTOM_ITEMS, PATTERN_LABEL, moveItem, restFor,
  type CatalogExercise, type CustomDay, type Equipment, type WorkoutDay,
} from '../../lib/program'
import { ExerciseImage } from '../../components/ExerciseImage'
import { AddExercise } from './AddExercise'

/**
 * Elle gün kurma ekranı.
 *
 * Buradaki asıl karar: "sıfırdan kur" dediğinde **boş sayfa açmıyoruz**,
 * üretilen günün kopyasıyla başlıyoruz. Sıfırdan isteyen zaten "Hepsini sil"e
 * basar; ama vakaların çoğunda insan "şunu çıkarayım, şunun setini artırayım"
 * diyor ve boş liste onu cezalandırıyor.
 *
 * Reçete alanları (set/tekrar/dinlenme) burada düzenlenebiliyor çünkü gün artık
 * üreticinin değil kullanıcının: kalıp reçetesini dayatmanın bir dayanağı kalmadı.
 * Yine de boş bırakılan alan kalıbın varsayılanına düşüyor.
 */
export function DayBuilder({ day, draft, catalog, equipment, onChange, onCancel, onSave }: {
  day: WorkoutDay
  draft: CustomDay
  catalog: CatalogExercise[]
  equipment: string[]
  onChange: (next: CustomDay) => void
  onCancel: () => void
  onSave: () => void
}) {
  const [adding, setAdding] = useState(false)
  const bySlug = new Map(catalog.map((c) => [c.slug, c]))
  const items = draft.items

  const patch = (i: number, p: Partial<CustomDay['items'][number]>) =>
    onChange({ ...draft, items: items.map((x, k) => (k === i ? { ...x, ...p } : x)) })

  const num = (v: string): number | undefined => {
    const n = Number(v)
    return v.trim() === '' || Number.isNaN(n) ? undefined : n
  }

  return (
    <div className="builder">
      <div className="builder-head">
        <div style={{ flex: '1 1 220px' }}>
          <label className="f">Gün adı</label>
          <input
            type="text"
            maxLength={40}
            placeholder={day.name ?? `Antrenman ${day.label}`}
            value={draft.name ?? ''}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
          />
        </div>
        <div className="builder-head-actions">
          <button className="primary" onClick={onSave}>Kaydet</button>
          <button onClick={onCancel}>Vazgeç</button>
        </div>
      </div>

      {items.length === 0 && (
        <p className="sub" style={{ margin: '14px 0' }}>
          Gün boş. Aşağıdan hareket ekle — kaydettiğinde bu gün tamamen senin
          listenden oluşacak.
        </p>
      )}

      <ol className="rows">
        {items.map((item, i) => {
          const ex = bySlug.get(item.slug)
          const unitSec = (ex?.pattern ?? '') === 'core'
          return (
            <li key={item.slug} className="rowline">
              <div className="rowline-order">
                <button aria-label="Yukarı taşı" disabled={i === 0}
                        onClick={() => onChange({ ...draft, items: moveItem(items, i, -1) })}>↑</button>
                <span className="rowline-no">{i + 1}</span>
                <button aria-label="Aşağı taşı" disabled={i === items.length - 1}
                        onClick={() => onChange({ ...draft, items: moveItem(items, i, 1) })}>↓</button>
              </div>

              {ex && (
                <ExerciseImage className="rowline-img" images={ex.images} pattern={ex.pattern} />
              )}

              <div className="rowline-main">
                <div className="rowline-name">{ex?.nameEn ?? item.slug}</div>
                <div className="rowline-meta">
                  {ex ? (EQUIPMENT_LABEL[ex.equipment as Equipment] ?? ex.equipment) : 'katalogda yok'}
                  {ex && ` · ${PATTERN_LABEL[ex.pattern as never] ?? ex.pattern}`}
                </div>
              </div>

              <div className="rowline-rx">
                <Field label="set" value={item.sets} placeholder="3"
                       onChange={(v) => patch(i, { sets: num(v) })} />
                <Field label={unitSec ? 'sn alt' : 'alt'} value={item.low} placeholder={unitSec ? '20' : '8'}
                       onChange={(v) => patch(i, { low: num(v) })} />
                <Field label={unitSec ? 'sn üst' : 'üst'} value={item.high} placeholder={unitSec ? '45' : '12'}
                       onChange={(v) => patch(i, { high: num(v) })} />
                <Field label="dinlenme" value={item.rest} placeholder={String(restFor(ex?.pattern ?? ''))}
                       onChange={(v) => patch(i, { rest: num(v) })} />
              </div>

              <button className="rowline-del" aria-label="Çıkar"
                      onClick={() => onChange({ ...draft, items: items.filter((_, k) => k !== i) })}>
                ×
              </button>
            </li>
          )
        })}
      </ol>

      <div className="builder-foot">
        <button className={adding ? '' : 'primary'}
                disabled={items.length >= MAX_CUSTOM_ITEMS && !adding}
                onClick={() => setAdding(!adding)}>
          {adding ? 'Ekleme listesini kapat' : '+ Hareket ekle'}
        </button>
        {items.length > 0 && (
          <button onClick={() => onChange({ ...draft, items: [] })}>Hepsini sil</button>
        )}
        <span className="note" style={{ margin: 0 }}>
          {items.length}/{MAX_CUSTOM_ITEMS} hareket
        </span>
      </div>

      {adding && (
        <AddExercise
          catalog={catalog}
          equipment={equipment}
          inDay={items.map((x) => x.slug)}
          count={items.length}
          limit={MAX_CUSTOM_ITEMS}
          onPick={(slug) => {
            onChange({ ...draft, items: [...items, { slug }] })
            setAdding(false)
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  )
}

/** Boş bırakılabilen sayı alanı — boş = kalıbın varsayılanı. */
function Field({ label, value, placeholder, onChange }: {
  label: string; value?: number; placeholder: string; onChange: (v: string) => void
}) {
  return (
    <label className="rx-field">
      <span>{label}</span>
      <input type="number" inputMode="numeric" min={0} max={999}
             placeholder={placeholder}
             value={value ?? ''}
             onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}
